import { useRef } from "react"

import { useObservable, useObservableState } from "observable-hooks"
import { EMPTY, map } from "rxjs"
import { distinctUntilChanged, switchMap } from "rxjs/operators"

import { ANONYMOUS_USER_ID } from "@treefort/constants"
import { useAuth } from "@treefort/lib/auth-provider"
import { simpleHash } from "@treefort/lib/simple-hash"

import { ConsumableContent } from "../lib/consumable-content"
import { activeProfileIdObservable } from "../lib/profiles"
import {
  ProgressItem,
  ProgressItemData,
  ProgressItemForConsumableContent,
  getProgressItemFromProgressData,
  getProgressItemKeyFromConsumableContent,
} from "../lib/progress-item"
import { progressStore } from "../watermelon/stores/progress"
import { useActiveProfileId } from "./use-active-profile-id"

const alwaysUpdate = () => true

export function useProgressItem<T extends ProgressItem>(params: {
  key: string | undefined
  /**
   * This function will be called for every change in progress. If it returns
   * false then useProgress will ignore the change and not trigger a re-render.
   * This function cannot be changed after the hook is first called.
   */
  shouldUpdate?: (prev: T | undefined, next: T | undefined) => boolean
}) {
  const { key } = params
  const auth = useAuth()
  const userId = auth.user?.id || ANONYMOUS_USER_ID
  const profileId = useActiveProfileId()
  const shouldUpdate = useRef(params.shouldUpdate || alwaysUpdate).current
  return useObservableState(
    useObservable(
      switchMap(([key, userId, profileId]) =>
        key
          ? progressStore.observeSingle(key, { userId, profileId }).pipe(
              map((progress) =>
                progress
                  ? (getProgressItemFromProgressData({
                      data: JSON.parse(progress.data),
                      profileId,
                    }) as T)
                  : undefined,
              ),
              distinctUntilChanged((prev, next) => !shouldUpdate(prev, next)),
            )
          : EMPTY,
      ),
      [key, userId, profileId],
    ),
    undefined,
  )
}

export function useProgressItemForConsumableContent<
  T extends ConsumableContent,
>({
  consumableContent,
  shouldUpdate,
}: {
  consumableContent: T | undefined
  shouldUpdate?: (
    prev: ProgressItemForConsumableContent<T> | undefined,
    next: ProgressItemForConsumableContent<T> | undefined,
  ) => boolean
}) {
  return useProgressItem<ProgressItemForConsumableContent<T>>({
    key:
      consumableContent &&
      getProgressItemKeyFromConsumableContent(consumableContent),
    shouldUpdate,
  })
}

/**
 * Subscribe to a list of progress items.
 *
 * NOTE: This emits when the contents of the list changes, but not when the
 * progress of a specific item is updated.
 */
export function useProgressItems({
  enabled,
  filterBy,
  limit,
}: {
  enabled: boolean
  filterBy?: (progressItem: ProgressItem) => boolean
  limit: number
}) {
  return useObservableState(
    useObservable(
      (observable) =>
        observable.pipe(
          switchMap(([enabled]) =>
            enabled
              ? activeProfileIdObservable.pipe(
                  switchMap((profileId) =>
                    progressStore
                      .observeAllByCurrentUser({ profileId, limit })
                      .pipe(
                        map((progress) =>
                          progress.flatMap((progress) => {
                            const data: ProgressItemData = JSON.parse(
                              progress.data,
                            )
                            const progressItem =
                              getProgressItemFromProgressData({
                                data,
                                profileId,
                              })
                            if (!filterBy || filterBy(progressItem)) {
                              return progressItem
                            }
                            return []
                          }),
                        ),
                        distinctUntilChanged(
                          (a, b) =>
                            a.length === b.length &&
                            a.every(
                              (item, index) =>
                                item.getKey() === b[index]?.getKey(),
                            ),
                        ),
                      ),
                  ),
                )
              : EMPTY,
          ),
        ),
      [enabled],
    ),
  )
}

export function useProgressItemCountsByContentType({
  limit,
  filterBy,
  enabled,
}: {
  limit: number
  filterBy?: (progressItem: ProgressItem) => boolean
  enabled?: boolean
}) {
  return useObservableState(
    useObservable(
      switchMap(([enabled]) =>
        enabled
          ? activeProfileIdObservable.pipe(
              switchMap((profileId) =>
                progressStore
                  .observeAllByCurrentUser({
                    profileId,
                    limit,
                  })
                  .pipe(
                    map((progress) =>
                      progress.reduce(
                        (count, progress) => {
                          const data = JSON.parse(
                            progress.data,
                          ) as ProgressItemData
                          const progressItem = getProgressItemFromProgressData({
                            data,
                            profileId,
                          })
                          if (!filterBy || filterBy(progressItem)) {
                            count[data.contentType] ||= 0
                            count[data.contentType] += 1
                          }
                          return count
                        },
                        {} as Record<string, number>,
                      ),
                    ),
                    distinctUntilChanged(
                      (a, b) => simpleHash(a) === simpleHash(b),
                    ),
                  ),
              ),
            )
          : EMPTY,
      ),
      [enabled],
    ),
  )
}
